import { endOfDay } from "date-fns";

import type {
	BranchViewModel,
	LayerConfiguration,
	LayerDefinition,
	LayerItemType,
	PositionViewModel,
	RetailLocationViewModel,
} from "../../../../models";

import { BreadcrumbsController } from "../../../../api/breadcrumbsController";
import { awaitsEnrollment, resolveDateRange, SALE_WINDOW_HOURS } from "../../layers/dateRange";
import { isOfficeLayer, isPositionLayer, isRetailLayer } from "../../layers/layerTypes";
import { LAYER_MAX_ITEMS } from "./constants";
import { hasExecuted } from "./serverFilters";

export type LayerItem = PositionViewModel | RetailLocationViewModel | BranchViewModel;

export interface LayerRequest {
	queryId: "positions" | "sales" | "retailLocations" | "offices";
	params: object;
	load: () => Promise<LayerItem[]>;
}

type ExecutedFilters = Partial<LayerConfiguration>;

function segmentParams(executed: ExecutedFilters) {
	return {
		brandIds: executed.brandIds,
		marketerIds: executed.marketerIds,
		channelIds: executed.channelIds,
		stateIds: executed.stateIds,
	};
}

export function buildPositionsParams(itemType: LayerItemType, executed: ExecutedFilters) {
	const { startDateTime, endDateTime } = resolveDateRange(executed);
	return {
		...segmentParams(executed),
		// BreadcrumbsController.getPositions and getSales send reps as repIds.
		reps: itemType === "Representatives" ? selectedIds(executed.reps) : undefined,
		machineIds: itemType === "Machines" ? selectedIds(executed.machines) : undefined,
		activeRepOnly: (itemType === "Representatives" && executed.activeRepOnly) || undefined,
		activeMachineOnly: (itemType === "Machines" && executed.activeMachineOnly) || undefined,
		enrollmentId: executed.enrollmentId,
		enrollmentWindowHours: executed.dateRange === "aroundSale" ? SALE_WINDOW_HOURS : undefined,
		minAccuracy: executed.minAccuracy,
		startDateTime,
		// The date pickers store midnight, but the server compares Timestamp <= endDateTime.
		endDateTime: endDateTime ? endOfDay(new Date(endDateTime)) : undefined,
		lastPositions: LAYER_MAX_ITEMS.toString(),
	};
}

export function buildSalesParams(executed: ExecutedFilters) {
	const { startDateTime, endDateTime } = resolveDateRange(executed);
	return {
		...segmentParams(executed),
		reps: selectedIds(executed.reps),
		machineIds: selectedIds(executed.machines),
		enrollmentId: executed.enrollmentId,
		startDateTime,
		endDateTime,
		// The server applies the good-sales filter whenever the parameter is present, even when false.
		goodSales: executed.goodSales || undefined,
		phoneCarrierType: executed.phoneCarrierType || undefined,
		lastPositions: LAYER_MAX_ITEMS.toString(),
	};
}

export function buildRetailLocationsParams(executed: ExecutedFilters) {
	return {
		marketerIds: executed.marketerIds,
		assignedUserIds: executed.assignedUserIds,
		assignedOfficeIds: executed.assignedOfficeIds,
		lastPositions: LAYER_MAX_ITEMS.toString(),
	};
}

export function buildLayerRequest(layer: LayerDefinition): LayerRequest | undefined {
	if (!hasExecuted(layer)) return undefined;
	const executed = layer.executedFilters;
	if (awaitsEnrollment(executed)) return undefined;

	if (isPositionLayer(layer.itemType)) {
		const params = buildPositionsParams(layer.itemType, executed);
		return { queryId: "positions", params, load: () => BreadcrumbsController.getPositions(params) };
	}
	if (layer.itemType === "Sales") {
		const params = buildSalesParams(executed);
		return { queryId: "sales", params, load: () => BreadcrumbsController.getSales(params) };
	}
	if (isRetailLayer(layer.itemType)) {
		const params = buildRetailLocationsParams(executed);
		return {
			queryId: "retailLocations",
			params,
			load: () =>
				BreadcrumbsController.getRetailLocations(
					undefined,
					params.marketerIds,
					params.assignedUserIds,
					params.assignedOfficeIds,
					params.lastPositions,
					null,
				),
		};
	}
	if (isOfficeLayer(layer.itemType)) {
		const params = { lastPositions: LAYER_MAX_ITEMS.toString() };
		return { queryId: "offices", params, load: () => BreadcrumbsController.getOffices(params.lastPositions) };
	}
	return undefined;
}

function selectedIds(ids: number[] | undefined) {
	return ids?.length ? ids : undefined;
}
